/* LIV lab reports (snippets/liv-coa.liquid, sections/liv-lab.liquid).
   Draws each report's chromatogram from its measured cannabinoids, and "runs" a report when it comes
   into view or is looked up: a scan line, the numbers counting up, the batch number resolving, the
   bars and trace drawing in, the "Not detected" stamp. In the lab section it shows one report at a
   time, with the product buttons, the batch-number lookup, ?batch= links and a[data-liv-batch] links
   from elsewhere on the page. Native APIs only. Every report reads complete without this script, and
   nothing moves for visitors who prefer reduced motion. */

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const narrow = window.matchMedia('(max-width: 600px)');

const esc = (s) => String(s).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/* ---------- Chromatogram ----------
   An illustration, not lab data: one peak per measured cannabinoid at its usual HPLC retention time
   (minutes), its height following the square root of the amount so small peaks stay visible. The
   trace is SVG; the labels are HTML on top of it, so they stay readable at any width. */
const RETENTION = { CBDV: 1.9, CBDA: 3.1, CBGA: 3.45, CBG: 3.7, CBD: 4.0, THCV: 4.55, CBN: 5.5, CBC: 7.6, THCA: 8.4 };
const THC_RETENTION = 6.5;

function drawChroma(fig) {
  const plot = fig.querySelector('.liv-chroma__plot');
  let mg = null;
  try {
    mg = JSON.parse(fig.dataset.cannabinoids || 'null');
  } catch {
    mg = null;
  }
  if (!plot || !mg || typeof mg !== 'object') return;

  // Labs write acids either way (CBDA, CBDa); the retention table is upper case
  const measured = Object.entries(mg).filter(([c, v]) => RETENTION[c.toUpperCase()] && typeof v === 'number' && v > 0);
  const max = Math.max(0, ...measured.map(([, v]) => v));
  if (!max) return;

  const W = 600;
  const VH = narrow.matches ? 200 : 124; // phones get a taller drawing, so the trace doesn't flatten
  const BASE = VH - 6;
  const H = BASE - 20;
  const [X0, X1, T0, T1, N, SIG] = [12, 588, 1, 9, 360, 0.06];
  const x = (t) => X0 + ((t - T0) / (T1 - T0)) * (X1 - X0);
  const at = (px, py) => `left:${((px / W) * 100).toFixed(2)}%;top:${((py / VH) * 100).toFixed(2)}%`;

  const peaks = measured.map(([c, v]) => ({ c, t: RETENTION[c.toUpperCase()], h: Math.sqrt(v / max) }));
  let d = '';
  for (let i = 0; i <= N; i++) {
    const t = T0 + ((T1 - T0) * i) / N;
    let v = 0.006 * Math.sin(t * 11.3) + 0.004 * Math.sin(t * 27.1 + 1.7); // a little baseline noise
    for (const p of peaks) v += p.h * Math.exp(-((t - p.t) ** 2) / (2 * SIG * SIG));
    d += `${i ? 'L' : 'M'}${x(t).toFixed(1)} ${(BASE - v * H).toFixed(1)}`;
  }

  const main = peaks.reduce((a, b) => (b.h > a.h ? b : a)).c;
  let lines = '';
  let labels = peaks
    .filter((p) => p.h >= 0.3)
    .map((p) => {
      const cls = p.c === main ? 'liv-chroma__label liv-chroma__label--main' : 'liv-chroma__label';
      return `<span class="${cls}" style="${at(x(p.t), BASE - p.h * H - 4)}">${esc(p.c)}</span>`;
    })
    .join('');
  // THC not detected: a dashed marker where its peak would be
  if (fig.dataset.thcNd) {
    const tx = x(THC_RETENTION).toFixed(1);
    const top = Math.round(BASE - H * 0.74);
    lines += `<line class="liv-chroma__nd" x1="${tx}" y1="${BASE - 2}" x2="${tx}" y2="${top}"/>`;
    labels += `<span class="liv-chroma__ndlabel" style="${at(x(THC_RETENTION), top)}">${esc(fig.dataset.thcNd)}</span>`;
  }
  const unit = esc(fig.dataset.unit || 'min');
  for (const t of [2, 4, 6, 8]) {
    lines += `<line class="liv-chroma__tick" x1="${x(t)}" y1="${BASE}" x2="${x(t)}" y2="${BASE + 5}"/>`;
    labels += `<span class="liv-chroma__axis" style="${at(x(t), VH)}">${t} ${unit}</span>`;
  }

  plot.innerHTML = `<svg viewBox="0 0 ${W} ${VH}" focusable="false">
      <path class="liv-chroma__area" d="${d}L${X1} ${BASE}L${X0} ${BASE}Z"/>
      <line class="liv-chroma__base" x1="${X0}" y1="${BASE}" x2="${X1}" y2="${BASE}"/>
      <path class="liv-chroma__line" d="${d}" pathLength="1"/>${lines}
    </svg>${labels}`;
  fig.hidden = false;
}

/* ---------- The "lab run" ---------- */
const tweens = new WeakMap(); // element → latest run, so a new run stops the last one's counter

function tween(el, ms, frame) {
  const token = {};
  tweens.set(el, token);
  const start = performance.now();
  const step = (now) => {
    if (tweens.get(el) !== token) return;
    const p = Math.min(1, (now - start) / ms);
    frame(p);
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** The number as the server wrote it, which is also where every animation ends. */
function finalText(el) {
  if (el.dataset.final === undefined) el.dataset.final = el.textContent;
  return el.dataset.final;
}

function countUp(el) {
  const end = parseFloat(el.dataset.count);
  if (!Number.isFinite(end)) return;
  const final = finalText(el);
  const decimals = Number(el.dataset.decimals) || 0;
  const suffix = el.dataset.suffix || '';
  tween(el, 1300, (p) => {
    el.textContent = p < 1 ? (end * (1 - (1 - p) ** 2)).toFixed(decimals) + suffix : final;
  });
}

const GLYPHS = '0123456789ABCDEFLIV';
function scramble(el) {
  const final = finalText(el);
  tween(el, 900, (p) => {
    if (p >= 1) {
      el.textContent = final;
      return;
    }
    const n = Math.floor(p * final.length);
    let s = final.slice(0, n);
    for (let i = n; i < final.length; i++) s += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
    el.textContent = s;
  });
}

function play(el, keyframes, options) {
  el.animate(keyframes, { duration: 600, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards', ...options });
}

/** Holds a report at the start of its run (zeroed numbers, empty bars) until it comes into view. */
function prime(report) {
  if (reduce) return;
  report.classList.add('liv-coa--primed');
  for (const el of report.querySelectorAll('[data-count]')) {
    finalText(el);
    el.textContent = (0).toFixed(Number(el.dataset.decimals) || 0) + (el.dataset.suffix || '');
  }
}

function runReport(report) {
  report.classList.remove('liv-coa--primed');
  if (reduce) {
    for (const el of report.querySelectorAll('[data-final]')) el.textContent = el.dataset.final;
    return;
  }
  report.classList.remove('is-scanning');
  void report.offsetWidth; // restart the scan line
  report.classList.add('is-scanning');
  report.querySelectorAll('[data-count]').forEach(countUp);
  const number = report.querySelector('[data-scramble]');
  if (number) scramble(number);
  report.querySelectorAll('.liv-coa__bar').forEach((bar, i) =>
    play(bar, [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
      duration: 900,
      delay: 150 + i * 40,
      easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
    })
  );
  const line = report.querySelector('.liv-chroma__line');
  if (line) play(line, [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 1500, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' });
  const area = report.querySelector('.liv-chroma__area');
  if (area) play(area, [{ opacity: 0 }, { opacity: 1 }], { duration: 800, delay: 800, easing: 'ease-out' });
  report.querySelectorAll('.liv-chroma__label, .liv-chroma__nd, .liv-chroma__ndlabel').forEach((el, i) =>
    play(el, [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], { duration: 400, delay: 1100 + i * 80 })
  );
  const stamp = report.querySelector('.liv-coa__stamp');
  if (stamp) {
    play(stamp, [{ opacity: 0, transform: 'scale(1.8) rotate(-10deg)' }, { opacity: 1, transform: 'none' }], {
      duration: 600,
      delay: 350,
      easing: 'cubic-bezier(0.34, 1.8, 0.64, 1)',
    });
  }
}

/* ---------- Running a report when it comes into view ---------- */
const seen = new WeakMap(); // observed element → what to run
const seenObserver =
  !reduce && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            seenObserver.unobserve(entry.target);
            seen.get(entry.target)?.();
            seen.delete(entry.target);
          }
        },
        { rootMargin: '0px 0px -22% 0px' }
      )
    : null;

/** Runs `report` once `target` is on screen: now if it already is, else primed until it arrives. */
function present(report, target = report) {
  if (!seenObserver) return runReport(report);
  if (target.getBoundingClientRect().top < window.innerHeight * 0.78) {
    seenObserver.unobserve(target);
    seen.delete(target);
    return runReport(report);
  }
  prime(report);
  seen.set(target, () => runReport(report));
  seenObserver.observe(target);
}

/* ---------- The lab section: one report at a time ---------- */
let activeLab = null;
const batchKey = (s) => String(s ?? '').trim().toUpperCase().replace(/\s+/g, '');

function initLab(root) {
  const section = root.closest('.liv-lab') ?? root;
  const reports = [...root.querySelectorAll('.liv-coa[data-batch]')];
  if (!reports.length) return;
  const buttons = [...section.querySelectorAll('[data-liv-lab-picker] [data-batch]')];
  const form = section.querySelector('[data-liv-lab-form]');
  const input = form?.querySelector('input');
  const missing = root.querySelector('[data-liv-missing]');
  const status = root.querySelector('[data-liv-lab-status]');
  const byKey = new Map(reports.map((r) => [batchKey(r.dataset.batch), r]));

  // "No match" lists the current batches to try
  const tries = missing?.querySelector('[data-liv-tries]');
  if (tries) {
    const numbers = (buttons.length ? buttons : reports).map((el) => el.dataset.batch);
    const nodes = [];
    numbers.forEach((n, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'liv-lab__try';
      b.dataset.batch = n;
      b.textContent = n;
      nodes.push(...(i ? [', ', b] : [b]));
    });
    tries.replaceChildren(...nodes, '.');
  }

  /** Shows the report for a batch number (the full number, or its last 4+ characters). */
  function show(raw, { announce = true } = {}) {
    let id = batchKey(raw);
    if (!byKey.has(id) && id.length >= 4) {
      const matches = [...byKey.keys()].filter((k) => k.endsWith(id));
      if (matches.length === 1) id = matches[0];
    }
    const report = byKey.get(id) ?? null;
    for (const r of reports) r.hidden = r !== report;
    if (missing) {
      missing.hidden = Boolean(report);
      const title = missing.querySelector('[data-template]');
      if (!report && title) title.textContent = title.dataset.template.replace('[batch]', () => id);
    }
    for (const b of buttons) b.setAttribute('aria-pressed', String(Boolean(report) && batchKey(b.dataset.batch) === id));
    if (input) input.value = report ? report.dataset.batch : id;
    if (announce && status) {
      status.textContent = report
        ? (root.dataset.msgShowing ?? '').replace('[product]', () => report.dataset.product ?? '').replace('[batch]', () => report.dataset.batch)
        : (root.dataset.msgMissing ?? '').replace('[batch]', () => id);
    }
    return report;
  }

  const pick = (raw) => {
    const report = show(raw);
    if (report) runReport(report);
  };
  for (const b of buttons) b.addEventListener('click', () => pick(b.dataset.batch));
  missing?.addEventListener('click', (e) => {
    const b = e.target instanceof Element ? e.target.closest('[data-batch]') : null;
    if (b) pick(b.dataset.batch);
  });
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    if (!batchKey(input.value)) {
      input.focus();
      return;
    }
    pick(input.value);
  });

  const lab = {
    section,
    /** Opens a batch from a link elsewhere on the page: scrolls to the section, then runs it. */
    open(raw) {
      const report = show(raw);
      section.scrollIntoView({ behavior: reduce ? 'instant' : 'smooth', block: 'start' });
      if (report) present(report, root);
    },
  };
  activeLab = lab;

  section.querySelector('[data-liv-lab-picker]')?.removeAttribute('hidden');
  form?.removeAttribute('hidden');

  const wanted = new URLSearchParams(window.location.search).get('batch');
  if (wanted) {
    requestAnimationFrame(() => lab.open(wanted));
    show(wanted, { announce: false });
  } else {
    const first = show(buttons[0]?.dataset.batch ?? reports[0].dataset.batch, { announce: false });
    if (first) present(first, root);
  }
}

/* ---------- Reports on their own (e.g. a product page) ---------- */
function initSoloReport(report) {
  const details = report.closest('details');
  if (details && !details.open) {
    prime(report);
    const onToggle = () => {
      if (!details.open) return;
      details.removeEventListener('toggle', onToggle);
      runReport(report);
    };
    details.addEventListener('toggle', onToggle);
    return;
  }
  present(report);
}

/* ---------- Boot ---------- */
function init(root) {
  for (const fig of root.querySelectorAll('[data-liv-chroma]')) drawChroma(fig);
  for (const lab of root.querySelectorAll('[data-liv-lab]')) {
    if (lab.dataset.livReady) continue;
    lab.dataset.livReady = 'true';
    initLab(lab);
  }
  for (const report of root.querySelectorAll('.liv-coa')) {
    if (report.dataset.livReady || report.closest('[data-liv-lab]')) continue;
    report.dataset.livReady = 'true';
    initSoloReport(report);
  }
}

init(document);

narrow.addEventListener('change', () => document.querySelectorAll('[data-liv-chroma]').forEach(drawChroma));

document.addEventListener('animationend', (e) => {
  if (e.animationName === 'liv-coa-scan' && e.target instanceof Element) e.target.classList.remove('is-scanning');
});

// Product cards and other links: a[data-liv-batch="LIV…"] opens that batch in the lab section
document.addEventListener('click', (e) => {
  const link = e.target instanceof Element ? e.target.closest('a[data-liv-batch]') : null;
  if (!link || !activeLab?.section.isConnected || e.defaultPrevented) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  activeLab.open(link.dataset.livBatch);
});

// The theme editor re-renders a section in place when its settings change
document.addEventListener('shopify:section:load', (e) => {
  if (e.target instanceof Element) init(e.target);
});
